import { morph } from "@theme/morph";
import { Component } from "@theme/component";
import { CartUpdateEvent, ThemeEvents } from "@theme/events";
import { DialogComponent, DialogCloseEvent } from "@theme/dialog";
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from "@theme/utilities";

export class QuickAddComponent extends Component {
    /** @type {AbortController | null} */
    #abortController = null;
    /** @type {Map<string, Element>} */
    #cachedContent = new Map();

    get productPageUrl() {
        // Allow explicit product URL via data attribute for contexts outside product-card (e.g., PDP overlay buttons)
        const explicit = this.dataset.productUrl;
        if (explicit) {
            try {
                const u = new URL(explicit, location.origin);
                return u.toString();
            } catch (_) {
                // fall back to card-derived URL
            }
        }

        const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest("product-card"));
        const productLink = productCard?.getProductCardLink();

        if (!productLink?.href) return "";

        const url = new URL(productLink.href);

        if (url.searchParams.has("variant")) {
            return url.toString();
        }

        const selectedVariantId = this.#getSelectedVariantId();
        if (selectedVariantId) {
            url.searchParams.set("variant", selectedVariantId);
        }

        return url.toString();
    }

    /**
     * Gets the currently selected variant ID from the product card
     * @returns {string | null} The variant ID or null
     */
    #getSelectedVariantId() {
        const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest("product-card"));
        return productCard?.getSelectedVariantId() || null;
    }

    connectedCallback() {
        super.connectedCallback();
        mediaQueryLarge.addEventListener("change", this.#closeQuickAddModal);
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        mediaQueryLarge.removeEventListener("change", this.#closeQuickAddModal);
        this.#abortController?.abort();
    }

    /**
     * Handles quick add button click
     * @param {Event} event - The click event
     */
    handleClick = async (event) => {
        event.preventDefault();
        const currentUrl = this.productPageUrl;

        // Check if we have cached content for this URL
        let productGrid = this.#cachedContent.get(currentUrl);

        if (!productGrid) {
            // Fetch and cache the content
            const html = await this.fetchProductPage(currentUrl);
            if (html) {
                const gridElement = html.querySelector("[data-product-grid-content]");
                if (gridElement) {
                    // Cache the cloned element to avoid modifying the original
                    productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
                    this.#cachedContent.set(currentUrl, productGrid);
                }
            }
        }

        if (productGrid) {
            // Use a fresh clone from the cache
            const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
            await this.updateQuickAddModal(freshContent);
        }

        this.#openQuickAddModal();
    };

    /** @param {QuickAddDialog} dialogComponent */
    #stayVisibleUntilDialogCloses(dialogComponent) {
        this.toggleAttribute("stay-visible", true);

        dialogComponent.addEventListener(
            DialogCloseEvent.eventName,
            () => this.toggleAttribute("stay-visible", false),
            {
                once: true,
            },
        );
    }

    #openQuickAddModal = () => {
        const dialogComponent = document.getElementById("quick-add-dialog");
        if (!(dialogComponent instanceof QuickAddDialog)) return;

        this.#stayVisibleUntilDialogCloses(dialogComponent);
        dialogComponent.showDialog();
    };

    #closeQuickAddModal = () => {
        const dialogComponent = document.getElementById("quick-add-dialog");
        if (!(dialogComponent instanceof QuickAddDialog)) return;

        dialogComponent.closeDialog();
    };

    /**
     * Fetches the product page content
     * @param {string} productPageUrl - The URL of the product page to fetch
     * @returns {Promise<Document | null>}
     */
    async fetchProductPage(productPageUrl) {
        if (!productPageUrl) return null;

        // We use this to abort the previous fetch request if it's still pending.
        this.#abortController?.abort();
        this.#abortController = new AbortController();

        try {
            const response = await fetch(productPageUrl, {
                signal: this.#abortController.signal,
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
            }

            const responseText = await response.text();
            const html = new DOMParser().parseFromString(responseText, "text/html");

            return html;
        } catch (error) {
            if (error.name === "AbortError") {
                return null;
            } else {
                throw error;
            }
        } finally {
            this.#abortController = null;
        }
    }

    /**
     * Re-renders the variant picker.
     * @param {Element} productGrid - The product grid element
     */
    async updateQuickAddModal(productGrid) {
        const modalContent = document.getElementById("quick-add-modal-content");

        if (!productGrid || !modalContent) return;

        if (isMobileBreakpoint()) {
            const productDetails = productGrid.querySelector(".product-details");
            if (!productDetails) return;
            const productFormComponent = productGrid.querySelector("product-form-component");
            const variantPicker = productGrid.querySelector("variant-picker");
            const productPrice = productGrid.querySelector("product-price");
            const productTitle = document.createElement("a");
            productTitle.textContent = this.dataset.productTitle || "";

            // Make product title as a link to the product page
            productTitle.href = this.productPageUrl;

            if (!productFormComponent || !variantPicker || !productPrice || !productTitle) return;

            const productHeader = document.createElement("div");
            productHeader.classList.add("product-header");

            productHeader.appendChild(productTitle);
            productHeader.appendChild(productPrice);
            productGrid.appendChild(productHeader);
            productGrid.appendChild(variantPicker);
            productGrid.appendChild(productFormComponent);
            productDetails.remove();
        }

        morph(modalContent, productGrid);

        this.#syncVariantSelection(modalContent);
        this.#ensureShippingNote(modalContent);
        this.#ensureWishlistCTA(modalContent);
        this.#updateShippingNote(modalContent);
        // Ensure CTA visibility matches current selection (ATC vs Notify)
        this.#ensureNotifyButton(modalContent);
        this.#syncNotifyCTA(modalContent);
        // React to variant changes inside the modal
        modalContent.addEventListener("change", (e) => {
            const tgt = e.target;
            if (tgt instanceof HTMLInputElement && tgt.type === "radio" && tgt.dataset.variantId) {
                this.#updateShippingNote(modalContent);
                this.#syncWishlistVariant(modalContent);
                this.#ensureNotifyButton(modalContent);
                this.#syncNotifyCTA(modalContent);
            }
        });
    }

    /**
     * Syncs the variant selection from the product card to the modal
     * @param {Element} modalContent - The modal content element
     */
    #syncVariantSelection(modalContent) {
        const selectedVariantId = this.#getSelectedVariantId();
        if (!selectedVariantId) return;

        // Find and check the corresponding input in the modal
        const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
        for (const input of modalInputs) {
            if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
                input.checked = true;
                input.dispatchEvent(new Event("change", { bubbles: true }));
                break;
            }
        }
    }

    /** Ensures a shipping note element exists after the variant picker */
    #ensureShippingNote(modalContent) {
        const picker = modalContent.querySelector(".product-details variant-picker, variant-picker");
        if (!picker) return;
        let note = modalContent.querySelector(".qa-shipping-note");
        if (!note) {
            note = document.createElement("div");
            note.className = "qa-shipping-note";
            // Insert after picker
            picker.parentElement?.insertBefore(note, picker.nextSibling);
        }
    }

    /** Reads selected variant and updates the shipping note text + icon */
    #updateShippingNote(modalContent) {
        const note = modalContent.querySelector(".qa-shipping-note");
        if (!note) return;
        const input = modalContent.querySelector('input[type="radio"][data-variant-id]:checked');
        const speed =
            input?.getAttribute("data-shipping-speed") ||
            (input?.getAttribute("data-ship-48h") === "true" ? "express" : "standard");
        const isFastOrExpress = speed === "express" || speed === "fast";
        note.innerHTML = "";
        const row = document.createElement("div");
        row.className = "qa-shipping-note__row";
        const icon = document.createElement("span");
        icon.className = "svg-wrapper qa-shipping-note__icon";
        const truckTpl = document.getElementById("wishlist-truck-icon")?.innerHTML;
        const fastDeliveryTpl = document.getElementById("wishlist-fast-delivery-icon")?.innerHTML;
        const boxTpl = document.getElementById("wishlist-box-icon")?.innerHTML;
        const fastDeliveryIconFallback =
            '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256"><path fill="currentColor" d="M177.6,184.7l-18.5-18.5l-5.4,5.4l21.2,21.2c1.5,1.5,3.9,1.5,5.4,0l40.8-40.8l-5.4-5.4L177.6,184.7z M32.4,213.2c-5.6,0-9.9-4.6-9.9-10.4V84.8h184.7v17.9h12.5V72.3h0c0-1.1-0.3-2.2-0.9-3.2l-30.6-38.9c-1.1-1.9-3.2-3-5.3-3h-30.6h-41.5H46.8c-2.2,0-4.2,1.2-5.3,3L10.9,69.1c-0.6,1-0.9,2.1-0.9,3.2h0v130.5c0,12.8,9.8,22.8,22.4,22.8h78.3h16.5v-12.5h-16.5H32.4z M119,39.6h33.2h27.1l26.5,32.7H119V39.6z M50.4,39.6h60.3v32.7H23.8L50.4,39.6z M187.4,111.6c-32.3,0-58.6,26.3-58.6,58.6c0,32.3,26.3,58.6,58.6,58.6s58.6-26.3,58.6-58.6C246,137.9,219.7,111.6,187.4,111.6z M187.4,220.5c-27.8,0-50.3-22.6-50.3-50.3s22.6-50.3,50.3-50.3s50.3,22.6,50.3,50.3S215.1,220.5,187.4,220.5z"/></svg>';
        icon.innerHTML =
            speed === "express"
                ? truckTpl || window.themeAssets?.iconTruck || ""
                : speed === "fast"
                  ? fastDeliveryTpl || fastDeliveryIconFallback
                  : boxTpl || window.themeAssets?.iconBox || "";
        // Fallback: inline minimal SVGs if no template or themeAssets
        if (!icon.innerHTML) {
            icon.innerHTML =
                speed === "express"
                    ? '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M3 6h10a1 1 0 0 1 1 1v5h2.586a1 1 0 0 1 .707.293l2.828 2.828A1 1 0 0 1 21 16v2a1 1 0 0 1-1 1h-1a2 2 0 1 1-4 0H9a2 2 0 1 1-4 0H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1Zm11 6V8H4v9h.17A2.999 2.999 0 0 1 9 18h6a3 3 0 0 1 4.83 0H20v-1.586L17.586 12H14Z"/></svg>'
                    : speed === "fast"
                      ? fastDeliveryIconFallback
                      : '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M21 8.5V18a2 2 0 0 1-1.106 1.789l-7 3.5a2 2 0 0 1-1.788 0l-7-3.5A2 2 0 0 1 3 18V8.5a2 2 0 0 1 1.106-1.789l7-3.5a2 2 0 0 1 1.788 0l7 3.5A2 2 0 0 1 21 8.5ZM12 4.118 5 7.618 12 11.118l7-3.5-7-3.5ZM5 9.382V18l7 3.5V12.882L5 9.382Zm9 12.118 5-2.5V9.382l-5 2.5V21.5Z"/></svg>';
        }
        const text = document.createElement("span");
        text.className = "qa-shipping-note__text";
        const shippingNoteFast = Theme?.translations?.shipping_note_fast || "Shipped within 48h";
        const shippingNoteFastDelivery =
            Theme?.translations?.shipping_note_fast_delivery || "Fast Delivery (3-4 workdays)";
        const shippingNoteNormal = Theme?.translations?.shipping_note_normal || "Shipped in 2-10 business days";
        text.textContent =
            speed === "express" ? shippingNoteFast : speed === "fast" ? shippingNoteFastDelivery : shippingNoteNormal;
        row.append(icon, text);
        note.appendChild(row);
    }

    /** Ensure an Add to wishlist button exists under the primary CTAs */
    #ensureWishlistCTA(modalContent) {
        const buttons = modalContent.querySelector(".buy-buttons-block .product-form-buttons");
        if (!buttons) return;
        let wl = buttons.querySelector(".qa-wishlist-btn");
        if (!wl) {
            wl = document.createElement("button");
            wl.type = "button";
            wl.className = "button button-secondary qa-wishlist-btn";
            wl.textContent = "Add to wishlist";
            wl.setAttribute("data-wishlist-button", "");
            // Enrich with product and variant context
            const { handle, id } = this.#getProductIdentity();
            if (handle) wl.setAttribute("data-product-handle", handle);
            if (id) wl.setAttribute("data-product-id", id);
            const v = this.#getSelectedVariantId();
            if (v) wl.setAttribute("data-variant-id", v);
            buttons.appendChild(wl);
        }
    }

    #syncWishlistVariant(modalContent) {
        const wl = modalContent.querySelector(".qa-wishlist-btn");
        if (!wl) return;
        const v = this.#getSelectedVariantId();
        if (v) wl.setAttribute("data-variant-id", v);
    }

    #getProductIdentity() {
        try {
            const url = new URL(this.productPageUrl, location.origin);
            const path = url.pathname;
            const idx = path.indexOf("/products/");
            if (idx >= 0) {
                const handle = path.substring(idx + "/products/".length).split("/")[0];
                // Try to read product id from modal content if available
                const idAttr = document
                    .querySelector("#quick-add-modal-content [data-product-id]")
                    ?.getAttribute("data-product-id");
                return { handle, id: idAttr || "" };
            }
        } catch (_) {}
        return { handle: "", id: "" };
    }

    /** Toggle Add to Cart vs Notify button based on selected variant availability in QA modal */
    #syncNotifyCTA(root) {
        try {
            const form = root.querySelector("product-form-component");
            if (!form) return;
            const atcBtn = form.querySelector('[ref="addToCartButton"]');
            const notifyBtn = form.querySelector(".button--notify");
            if (!(atcBtn && notifyBtn)) return;

            const selected = root.querySelector('input[type="radio"][data-variant-id]:checked');
            const isOOS = !!(
                selected &&
                (selected.getAttribute("aria-disabled") === "true" ||
                    selected.getAttribute("aria-disabled") === "disabled" ||
                    selected.dataset.optionAvailable === "false" ||
                    selected.hasAttribute("disabled"))
            );

            if (isOOS) {
                atcBtn.style.display = "none";
                notifyBtn.style.removeProperty("display");
            } else {
                notifyBtn.style.display = "none";
                atcBtn.style.removeProperty("display");
            }
        } catch (_) {}
    }

    /** Ensure a Notify button exists; create fallback if absent */
    #ensureNotifyButton(root) {
        try {
            const form = root.querySelector("product-form-component");
            if (!form) return;
            let notifyBtn = form.querySelector(".button--notify");
            const atcBtn = form.querySelector('[ref="addToCartButton"]');
            if (!notifyBtn && atcBtn) {
                notifyBtn = document.createElement("button");
                notifyBtn.type = "button";
                notifyBtn.className = "button button--notify button--soldout";
                notifyBtn.style.display = "none";
                notifyBtn.textContent = "Sold out, click to be notified";
                notifyBtn.addEventListener(
                    "click",
                    (e) => {
                        e.preventDefault();
                        const selected = root.querySelector('input[type="radio"][data-variant-id]:checked');
                        const variantId = selected?.getAttribute("data-variant-id") || "";
                        const sizeName = selected?.value || selected?.getAttribute("aria-label") || "";
                        if (typeof window.openNotifyModal === "function") {
                            window.openNotifyModal(variantId, sizeName);
                        }
                    },
                    true,
                );
                atcBtn.parentElement?.insertAdjacentElement("afterend", notifyBtn);
                // Injected fallback Notify button into Quick Add form
            }
        } catch (err) {
            // ignore
        }
    }
}

if (!customElements.get("quick-add-component")) {
    customElements.define("quick-add-component", QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
    #abortController = new AbortController();

    connectedCallback() {
        super.connectedCallback();

        this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
        this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

        this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        this.#abortController.abort();
        this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    }

    /**
     * Closes the dialog
     * @param {CartUpdateEvent} event - The cart update event
     */
    handleCartUpdate = (event) => {
        if (event.detail.data.didError) return;
        this.closeDialog();
    };

    #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
        const anchorElement = /** @type {HTMLAnchorElement} */ (
            event.detail.data.html?.querySelector(".view-product-title a")
        );
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector(".view-product-title a"));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector(".product-header a"));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
    };

    #handleDialogClose = () => {
        const iosVersion = getIOSVersion();
        /**
         * This is a patch to solve an issue with the UI freezing when the dialog is closed.
         * To reproduce it, use iOS 16.0.
         */
        if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

        requestAnimationFrame(() => {
            /** @type {HTMLElement | null} */
            const grid = document.querySelector("#ResultsList [product-grid-view]");
            if (grid) {
                const currentWidth = grid.getBoundingClientRect().width;
                grid.style.width = `${currentWidth - 1}px`;
                requestAnimationFrame(() => {
                    grid.style.width = "";
                });
            }
        });
    };
}

if (!customElements.get("quick-add-dialog")) {
    customElements.define("quick-add-dialog", QuickAddDialog);
}
